"use client"

import { useEffect, useRef } from "react"
import { blobMediaUrl } from "@/lib/blob-url"

export interface HeroVideoSource {
  src: string
  type: string
}

interface HeroBackgroundVideoProps {
  /** Portrait crop served under 768px. */
  mobile: HeroVideoSource
  /** Desktop sources in preference order (e.g. AV1 first, H.264 fallback). */
  desktop: HeroVideoSource[]
  className?: string
}

/**
 * Muted looping background video that sits over a server-preloaded hero
 * poster. Sources attach only after the page is idle, so the poster keeps the
 * LCP slot, and never on Save-Data. Same loading contract as the membership
 * hero, which ships the reel this was extracted for.
 */
export function HeroBackgroundVideo({
  mobile,
  desktop,
  className = "absolute inset-0 z-0 h-full w-full object-cover",
}: HeroBackgroundVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const conn = (navigator as { connection?: { saveData?: boolean } })
      .connection
    if (conn?.saveData) return

    const schedule =
      "requestIdleCallback" in window
        ? (cb: () => void) => window.requestIdleCallback(cb, { timeout: 2000 })
        : (cb: () => void) => window.setTimeout(cb, 1000)

    schedule(() => {
      const isMobile = window.matchMedia("(max-width: 767px)").matches
      for (const { src, type } of isMobile ? [mobile] : desktop) {
        const source = document.createElement("source")
        source.src = blobMediaUrl(src)
        source.type = type
        video.appendChild(source)
      }
      video.load()
    })
    // Sources are static per page; attach once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <video
      ref={videoRef}
      autoPlay
      muted
      loop
      playsInline
      preload="none"
      aria-hidden="true"
      data-testid="hero-background-video"
      className={className}
    />
  )
}
